import { translate } from './i18n'
import type {
  DashboardStats,
  AlertTimeSeries,
  ActionDistribution,
  CrawlReportSummary,
  CrawlReportDetail,
  SuspiciousDomain,
  PaymentIntegration,
  UnauthorizedMerchantDto,
  CountryRuleRow,
  QueueStatsData,
  MonitoredBrand,
  BrandScanResult,
  SmishingReport,
  MerchantRow,
  MerchantBrandAuthRow,
  BrandAuthStatus,
  BrandAuthImportRow,
  BrandAuthImportReport,
  TriageKind,
  TriageStatus,
  TriageSummary,
  TriageVocabulary,
  TriageLogEntry,
  PortfolioReport,
  ImportResult,
  PaginatedResponse,
  ReportsQuery,
  DiscoveryQuery,
  WebScanQuery,
  WebScanCoverage,
  Organization,
  OrgStats,
  GlobalOrgStats,
  OrgPlan,
} from './types'

export interface ScanStatus {
  domains: boolean
  apps: boolean
  social: boolean
  payments: boolean
  webscan: boolean
}

const BASE = import.meta.env.VITE_API_URL ?? '/api'

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('icc_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/**
 * Rend lisible le corps d'une reponse en erreur.
 *
 * NestJS repond `{ message: string | string[], error, statusCode }`. Affiche tel quel,
 * ce JSON arrivait brut sous les yeux de l'utilisateur, qui devait y lire sa propre
 * erreur de saisie. On ne garde que le message, et le code HTTP ne sert plus que de
 * repli quand le corps n'apprend rien.
 */
function messageErreur(status: number, corps: string): string {
  try {
    const parsed = JSON.parse(corps) as { message?: string | string[] }
    const msg = parsed.message
    // Plusieurs messages sont joints par une espace : le bandeau d'erreur est un
    // paragraphe, un saut de ligne n'y serait pas rendu.
    if (Array.isArray(msg) && msg.length > 0) return msg.join(' ')
    if (typeof msg === 'string' && msg.length > 0) return msg
  } catch {
    // Corps non JSON (page d'erreur d'un proxy, reponse vide) : on retombe plus bas.
  }
  // Les replis passent par le catalogue : ils s'affichent tels quels dans
  // l'interface. Le `message` du serveur, lui, reste dans sa langue — c'est au
  // serveur de le traduire, et le recenser est un lot à part.
  if (status === 403) return translate('api.forbidden')
  if (status === 404) return translate('api.notFound')
  if (status >= 500) return translate('api.server')
  return corps.trim() || translate('api.status', { status })
}

/**
 * Traitement commun a toutes les methodes : une session expiree doit deconnecter
 * l'utilisateur quelle que soit la methode HTTP, pas seulement sur les GET.
 */
async function handleResponse<T>(res: Response): Promise<T> {
  if (res.status === 401) {
    const hadToken = !!localStorage.getItem('icc_token')
    localStorage.removeItem('icc_token')
    localStorage.removeItem('icc_user')
    if (hadToken) window.location.reload()
    throw new Error(translate('api.sessionExpired'))
  }
  if (!res.ok) {
    throw new Error(messageErreur(res.status, await res.text()))
  }
  const text = await res.text()
  if (!text) return undefined as T
  return JSON.parse(text) as T
}

async function get<T>(path: string, params?: Record<string, unknown>): Promise<T> {
  const url = new URL(`${BASE}${path}`, window.location.origin)
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') {
        url.searchParams.set(k, String(v))
      }
    })
  }

  if (_scopedOrgId && !url.searchParams.has('orgId') && !path.startsWith('/organizations')) {
    url.searchParams.set('orgId', _scopedOrgId)
  }

  return handleResponse<T>(await fetch(url.toString(), { headers: authHeaders() }))
}

export const api = {
  // ── Stats ──────────────────────────────────────────────
  getStats: (days = 7) =>
    get<DashboardStats>('/dashboard/stats', { days }),

  getTimeSeries: (days = 7) =>
    get<AlertTimeSeries[]>('/dashboard/stats/timeseries', { days }),

  getActionDistribution: (hours = 24) =>
    get<ActionDistribution[]>('/dashboard/stats/actions', { hours }),

  getQueueStats: () =>
    get<QueueStatsData>('/dashboard/stats/queue'),

  // ── Rapports ───────────────────────────────────────────
  getReports: (query: ReportsQuery = {}) =>
    get<PaginatedResponse<CrawlReportSummary>>('/dashboard/reports', query as any),

  getReportById: (id: string) =>
    get<CrawlReportDetail>(`/dashboard/reports/${id}`),

  // ── Découverte ─────────────────────────────────────────
  getSuspiciousDomains: (query: DiscoveryQuery = {}) =>
    get<PaginatedResponse<SuspiciousDomain>>('/dashboard/discovery', query as any),

  // ── Intégrations paiement ─────────────────────────────
  getPaymentIntegrations: (query: { brand?: string; complianceStatus?: string; merchantMatchStatus?: string; page?: number; limit?: number } = {}) =>
    get<PaginatedResponse<PaymentIntegration>>('/dashboard/payment-integrations', query as any),

  // ── Scan mondial N4 ───────────────────────────────────
  getWebScanResults: (query: WebScanQuery = {}) =>
    get<PaginatedResponse<UnauthorizedMerchantDto>>('/dashboard/webscan', query as Record<string, unknown>),

  getWebScanCountsByCountry: () =>
    get<Record<string, number>>('/dashboard/webscan/counts'),

  getWebScanCoverage: () =>
    get<WebScanCoverage>('/dashboard/webscan/coverage'),

  // ── Règles ─────────────────────────────────────────────
  getRulesMatrix: () =>
    get<CountryRuleRow[]>('/dashboard/rules'),

  updateRule: (activity: string, country: string, level: string) =>
    patch<CountryRuleRow[]>('/dashboard/rules', { activity, country, level }),

  // ── Statut des scans ───────────────────────────────────
  getScanStatus: () =>
    get<ScanStatus>('/discovery/scan-status'),

  // ── Déclenchement de scans ────────────────────────────
  triggerScanAll: () =>
    post<{ total: number; saved: number }>('/discovery/scan', {}),

  triggerScanApps: () =>
    post<{ total: number; saved: number }>('/discovery/scan-apps', {}),

  triggerScanSocial: () =>
    post<{ total: number; saved: number }>('/discovery/scan-social', {}),

  triggerScanPayments: () =>
    post<{ total: number; undeclared: number }>('/discovery/scan-payments', {}),

  triggerScanWeb: (country?: string) => {
    const params = country ? `?country=${country}` : ''
    return post<{ totalResults?: number; total?: number }>(`/discovery/scan-web${params}`, {})
  },
}

// ─── Helpers POST/PATCH/DELETE ────────────────────────────────────────────────

async function post<T>(path: string, body: unknown): Promise<T> {
  return handleResponse<T>(
    await fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    }),
  )
}

async function patch<T>(path: string, body: unknown): Promise<T> {
  return handleResponse<T>(
    await fetch(`${BASE}${path}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    }),
  )
}

async function del<T = void>(path: string): Promise<T> {
  return handleResponse<T>(
    await fetch(`${BASE}${path}`, { method: 'DELETE', headers: authHeaders() }),
  )
}

// ─── Brand API (CRUD marques surveillées) ─────────────────────────────────────

/**
 * Rapports exportables.
 *
 * Les exports sont des téléchargements de fichiers, pas du JSON : on passe par
 * un blob authentifié plutôt qu'un lien direct, le jeton devant voyager dans
 * l'en-tête et non dans l'URL.
 */
export const reportApi = {
  getPortfolio: (month?: string) =>
    get<PortfolioReport>('/reports/portfolio', month ? { month } : undefined),

  /** Déclenche le téléchargement du bilan mensuel en PDF. */
  downloadPortfolioPdf: async (month?: string): Promise<void> => {
    const qs = month ? `?month=${encodeURIComponent(month)}` : ''
    await downloadFile(`/reports/portfolio.pdf${qs}`, `surveillance-${month ?? 'mois-en-cours'}.pdf`)
  },

  /** Déclenche le téléchargement des détections au format CSV. */
  downloadDetectionsCsv: async (kind: TriageKind): Promise<void> => {
    const stamp = new Date().toISOString().slice(0, 10)
    await downloadFile(`/reports/detections/${kind}.csv`, `detections-${kind}-${stamp}.csv`)
  },
}

/** Récupère un fichier avec le jeton en en-tête, puis le remet au navigateur. */
async function downloadFile(path: string, filename: string): Promise<void> {
  const res = await fetch(`${BASE}${path}`, { headers: authHeaders() })
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`)

  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Laisser au navigateur le temps de lancer le telechargement.
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

/**
 * File de traitement des détections (N2 et N4).
 * L'agent inscrit au journal vient du jeton côté serveur : jamais d'ici.
 */
export const triageApi = {
  getVocabulary: () =>
    get<TriageVocabulary>('/triage/vocabulary'),

  getSummary: (kind: TriageKind) =>
    get<TriageSummary>(`/triage/${kind}/summary`),

  getLog: (kind: TriageKind, id: string) =>
    get<TriageLogEntry[]>(`/triage/${kind}/${id}/log`),

  changeStatus: (kind: TriageKind, id: string, status: TriageStatus, comment?: string) =>
    patch<{ triageStatus: TriageStatus; assignedTo: string | null; resolved: boolean }>(
      `/triage/${kind}/${id}/status`,
      { status, comment },
    ),

  assign: (kind: TriageKind, id: string, assignee: string | null) =>
    patch<{ assignedTo: string | null }>(`/triage/${kind}/${id}/assign`, { assignee }),

  bulkStatus: (kind: TriageKind, ids: string[], status: TriageStatus, comment?: string) =>
    post<{ updated: number; errors: Array<{ id: string; error: string }> }>(
      `/triage/${kind}/bulk-status`,
      { ids, status, comment },
    ),
}

export const brandApi = {
  findAll: (activeOnly = false) =>
    get<MonitoredBrand[]>('/brands', { activeOnly }),

  findById: (id: string) =>
    get<MonitoredBrand>(`/brands/${id}`),

  create: (data: Partial<MonitoredBrand>) =>
    post<MonitoredBrand>('/brands', data),

  update: (id: string, data: Partial<MonitoredBrand>) =>
    patch<MonitoredBrand>(`/brands/${id}`, data),

  toggle: (id: string) =>
    patch<MonitoredBrand>(`/brands/${id}/toggle`, {}),

  remove: (id: string) =>
    del(`/brands/${id}`),

  getStats: () =>
    get<{ total: number; active: number; byCategory: { category: string; count: number }[] }>('/brands/stats'),

  scan: (id: string) =>
    post<BrandScanResult>(`/discovery/scan/${id}`, {}),

  /**
   * Import en masse des autorisations marchand × marque.
   * `apply` absent ou faux = simulation, rien n'est écrit.
   */
  importAuthorizations: (rows: BrandAuthImportRow[], apply = false) =>
    post<BrandAuthImportReport>('/brands/authorizations/import', { rows, apply }),

  // ── Marchands autorises par marque ──────────────────────
  getMerchants: (brandId: string) =>
    get<MerchantBrandAuthRow[]>(`/brands/${brandId}/merchants`),

  addMerchant: (brandId: string, data: { merchantId: string; contractRef?: string }) =>
    post<MerchantBrandAuthRow>(`/brands/${brandId}/merchants`, data),

  updateMerchantAuth: (brandId: string, merchantId: string, data: { status?: BrandAuthStatus; contractRef?: string; notes?: string }) =>
    patch<MerchantBrandAuthRow>(`/brands/${brandId}/merchants/${merchantId}`, data),

  removeMerchant: (brandId: string, merchantId: string) =>
    del(`/brands/${brandId}/merchants/${merchantId}`),
}

// ─── Merchant API (CRUD marchands) ──────────────────────────────────────────

export const merchantApi = {
  findAll: () =>
    get<MerchantRow[]>('/merchants'),

  findById: (id: string) =>
    get<MerchantRow>(`/merchants/${id}`),

  create: (data: { name: string; url: string; country: string; category: string; crawlFrequencyHours?: number }) =>
    post<MerchantRow>('/merchants', data),

  update: (id: string, data: Partial<MerchantRow>) =>
    patch<MerchantRow>(`/merchants/${id}`, data),

  suspend: (id: string) =>
    post<MerchantRow>(`/merchants/${id}/suspend`, {}),

  activate: (id: string) =>
    post<MerchantRow>(`/merchants/${id}/activate`, {}),

  remove: (id: string) =>
    del(`/merchants/${id}`),

  importCsv: (merchants: Array<{ name: string; url: string; country: string; category: string; crawlFrequencyHours?: number }>) =>
    post<ImportResult>('/merchants/import', { merchants }),

  crawlAll: () =>
    post<{ enqueued: number; message: string }>('/merchants/crawl-all', {}),

  triggerCrawl: (merchantId: string) =>
    post<{ jobId: string; message: string }>(`/merchants/${merchantId}/crawl`, {}),

  getCrawlProgress: () =>
    get<{ waiting: number; active: number; completed: number; failed: number; delayed: number }>('/merchants/crawl-progress'),

  getJobStatus: (jobId: string) =>
    get<{ state: string; progress: number }>(`/merchants/job-status/${jobId}`),

  // ── Autorisations de marques pour un marchand ──────────
  getBrands: (merchantId: string) =>
    get<MerchantBrandAuthRow[]>(`/merchants/${merchantId}/brands`),

  addBrand: (merchantId: string, data: { brandId: string; contractRef?: string }) =>
    post<MerchantBrandAuthRow>(`/merchants/${merchantId}/brands`, data),

  updateBrandAuth: (merchantId: string, brandId: string, data: { status?: BrandAuthStatus; contractRef?: string; notes?: string }) =>
    patch<MerchantBrandAuthRow>(`/merchants/${merchantId}/brands/${brandId}`, data),

  removeBrand: (merchantId: string, brandId: string) =>
    del(`/merchants/${merchantId}/brands/${brandId}`),
}

// ─── Ad Detection API (controle fiscal des annonceurs) ────────────────────────

export const adDetectionApi = {
  getAdvertisers: (query: Record<string, any> = {}) =>
    get<import('./types').PaginatedResponse<import('./types').DetectedAdvertiserRow>>('/ad-detection/advertisers', query),

  getStats: (country?: string) =>
    get<import('./types').AdDetectionStats>('/ad-detection/stats', { country }),

  /** État du quota SerpAPI depuis le démarrage du serveur (réservé aux administrateurs). */
  getSerpApiQuota: () =>
    get<{ configured: boolean; exhausted: boolean; callsMade: number; exhaustedAt: string | null; callsByCaller: Record<string, number> }>('/discovery/serpapi-quota'),

  getCountsByCountry: () =>
    get<Record<string, number>>('/ad-detection/counts-by-country'),

  getCategories: () =>
    get<string[]>('/ad-detection/categories'),

  getScanStatus: () =>
    get<import('./types').AdScanStatus>('/ad-detection/scan-status'),

  getConfig: () =>
    get<import('./types').AdDetectionConfig>('/ad-detection/config'),

  scanCountry: (country: string, searchTerms?: string[]) =>
    post<{ meta: number; google: number; total: number; persisted: number; newAdvertisers: number }>(
      '/ad-detection/scan',
      { country, searchTerms },
    ),

  updateFiscalStatus: (id: string, fiscalStatus: string, notes?: string) =>
    patch<import('./types').DetectedAdvertiserRow>(
      `/ad-detection/advertisers/${id}/fiscal-status`,
      { fiscalStatus, notes },
    ),

  /** L'auteur du changement est déduit du jeton ; `assignedTo` désigne l'agent en charge. */
  updateVerificationStatus: (
    id: string,
    status: import('./types').VerificationStatus,
    comment: string,
    assignedTo: string,
  ) =>
    patch<import('./types').DetectedAdvertiserRow>(
      `/ad-detection/advertisers/${id}/verification`,
      { status, comment, assignedTo },
    ),

  getHistory: (query: { country?: string; page?: number; limit?: number } = {}) =>
    get<import('./types').PaginatedResponse<import('./types').AdScanHistoryRow>>('/ad-detection/history', query),

  getEvolution: (country?: string, days = 90) =>
    get<import('./types').AdEvolutionPoint[]>('/ad-detection/evolution', { country, days }),

  getExportUrl: (country: string) =>
    `${BASE}/ad-detection/export/${country}`,

  getQueries: () =>
    get<import('./types').AdSearchQuery[]>('/ad-detection/queries'),

  createQuery: (query: string, category: string, countries: string[] = []) =>
    post<import('./types').AdSearchQuery>('/ad-detection/queries', { query, category, countries }),

  updateQuery: (id: string, data: { query?: string; category?: string; countries?: string[]; isActive?: boolean }) =>
    patch<import('./types').AdSearchQuery>(`/ad-detection/queries/${id}`, data),

  deleteQuery: (id: string) =>
    del(`/ad-detection/queries/${id}`),

  // ── Banner Detection ──────────────────────────────────────
  getBannerScanStatus: () =>
    get<import('./types').AdScanStatus>('/ad-detection/banner/scan-status'),

  /** Site en cours de visite. Une route ordinaire : `EventSource` ne sait pas s'authentifier. */
  getBannerProgress: () =>
    get<{ progress: import('./types').BannerScanProgress | null }>('/ad-detection/banner/progress'),

  scanBanners: (country: string, maxInternalPages?: number) =>
    post<import('./types').BannerScanResult>('/ad-detection/banner/scan', { country, maxInternalPages }),

  abortBannerScan: (country: string) =>
    post<{ aborted: boolean; country: string }>('/ad-detection/banner/scan/abort', { country }),

  getBannerTargets: (country?: string) =>
    get<import('./types').BannerScanTarget[]>('/ad-detection/banner/targets', country ? { country } : {}),

  createBannerTarget: (data: { url: string; country: string; name: string; category: import('./types').ScanTargetCategory }) =>
    post<import('./types').BannerScanTarget>('/ad-detection/banner/targets', data),

  updateBannerTarget: (id: string, data: Partial<{ url: string; name: string; category: string; isActive: boolean }>) =>
    patch<import('./types').BannerScanTarget>(`/ad-detection/banner/targets/${id}`, data),

  deleteBannerTarget: (id: string) =>
    del(`/ad-detection/banner/targets/${id}`),

  importBannerTargets: (rows: Array<{ url: string; country: string; name?: string; category?: string }>) =>
    post<{ imported: number; skipped: number; errors: Array<{ row: number; url: string; reason: string }> }>('/ad-detection/banner/targets/import', { rows }),

  // ── Banner Detection History ───────────────────────────────
  getBannerDetections: (query?: { country?: string; advertiserName?: string; siteDomain?: string; page?: number; limit?: number }) =>
    get<import('./types').PaginatedResponse<import('./types').BannerDetectionRow>>('/ad-detection/banner/detections', query as Record<string, string>),

  getBannerDetectionStats: (country?: string) =>
    get<import('./types').BannerDetectionStats>('/ad-detection/banner/detections/stats', country ? { country } : {}),

  getDetectionsByAdvertiser: (name: string, country?: string) =>
    get<import('./types').BannerDetectionRow[]>(`/ad-detection/banner/detections/advertiser/${encodeURIComponent(name)}`, country ? { country } : {}),

  resetCountry: (country: string) =>
    del<{ country: string; advertisersDeleted: number; historyDeleted: number; detectionsDeleted: number }>(`/ad-detection/reset/${country}`),
}

// ─── Smishing API (signalements SMS) ────────────────────────────────────────

export const smishingApi = {
  getReports: (opts: { page?: number; limit?: number; status?: string } = {}) =>
    get<PaginatedResponse<SmishingReport>>('/reports/smishing', opts),
}

// ─── Organization API (SUPERADMIN) ──────────────────────────────────────────

export const orgApi = {
  findAll: () =>
    get<Organization[]>('/organizations'),

  findById: (id: string) =>
    get<Organization>(`/organizations/${id}`),

  getStats: () =>
    get<GlobalOrgStats>('/organizations/stats'),

  getOrgStats: (id: string) =>
    get<OrgStats>(`/organizations/${id}/stats`),

  getOrgUsers: (id: string) =>
    get<import('./types').User[]>(`/organizations/${id}/users`),

  create: (data: { name: string; slug: string; countries: string[]; plan?: OrgPlan; maxUsers?: number; maxCountries?: number; contactEmail: string; contactName?: string }) =>
    post<Organization>('/organizations', data),

  update: (id: string, data: Partial<{ name: string; slug: string; countries: string[]; plan: OrgPlan; maxUsers: number; maxCountries: number; contactEmail: string; contactName: string; isActive: boolean }>) =>
    patch<Organization>(`/organizations/${id}`, data),

  remove: (id: string) =>
    del(`/organizations/${id}`),
}

// ─── Scoped orgId for SUPERADMIN ─────────────────────────────────────────────

let _scopedOrgId: string | null = null

export function setScopedOrgId(orgId: string | null) {
  _scopedOrgId = orgId
}

export function getScopedOrgId(): string | null {
  return _scopedOrgId
}

// ─── Auth API ─────────────────────────────────────────────────────────────────

export const authApi = {
  login: (email: string, password: string) =>
    post<import('./types').LoginResponse>('/auth/login', { email, password }),

  me: () =>
    get<import('./types').User>('/auth/me'),

  getUsers: () =>
    get<import('./types').User[]>('/auth/users'),

  /** Agents actifs de l'organisation, désignables sur un dossier (tout rôle). */
  getAgents: () =>
    get<Array<{ id: string; fullName: string; email: string }>>('/auth/agents'),

  createUser: (data: { email: string; fullName: string; password: string; role: import('./types').UserRole; organizationId?: string | null; allowedCountries?: string[]; allowedTabs?: string[] }) =>
    post<import('./types').User>('/auth/users', data),

  updateUser: (id: string, data: Record<string, unknown>) =>
    patch<import('./types').User>(`/auth/users/${id}`, data),

  deleteUser: (id: string) =>
    del(`/auth/users/${id}`),

  changePassword: (currentPassword: string, newPassword: string) =>
    patch<import('./types').User>('/auth/me/password', { currentPassword, newPassword }),
}

// ─── Paris & jeux ─────────────────────────────────────────────────────────────

/** URL absolue d'une route, avec le périmètre d'organisation du SUPERADMIN. */
function scopedUrl(path: string, params?: Record<string, unknown>): string {
  const url = new URL(`${BASE}${path}`, window.location.origin)
  Object.entries(params ?? {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v))
  })
  if (_scopedOrgId && !url.searchParams.has('orgId')) url.searchParams.set('orgId', _scopedOrgId)
  return url.toString()
}

export const bettingApi = {
  vocabulary: () => get<import('./types').BettingVocabulary>('/betting/vocabulary'),

  // Services
  listServices: (country?: string, includeFixtures?: boolean) =>
    get<import('./types').BettingService[]>('/betting/services', {
      country,
      includeFixtures: includeFixtures ? '1' : undefined,
    }),
  createService: (data: Partial<import('./types').BettingService>) =>
    post<import('./types').BettingService>('/betting/services', data),
  updateService: (id: string, data: Partial<import('./types').BettingService>) =>
    patch<import('./types').BettingService>(`/betting/services/${id}`, data),
  deleteService: (id: string) => del(`/betting/services/${id}`),
  runJourney: (id: string) => post<import('./types').PaymentTestDetail>(`/betting/services/${id}/run`, {}),

  // Comptes de test
  listAccounts: (serviceId: string) =>
    get<import('./types').BettingTestAccount[]>(`/betting/services/${serviceId}/accounts`),
  createAccount: (
    serviceId: string,
    data: { label: string; username: string; password: string; loginUrl?: string | null; testPhoneNumber?: string | null },
  ) => post<{ id: string }>(`/betting/services/${serviceId}/accounts`, data),
  updateAccount: (id: string, data: Record<string, unknown>) => patch<{ id: string }>(`/betting/accounts/${id}`, data),
  deleteAccount: (id: string) => del(`/betting/accounts/${id}`),

  // Tests
  listTests: (filters: import('./types').BettingTestFilters) =>
    get<PaginatedResponse<import('./types').PaymentTestRow>>('/betting/tests', filters as Record<string, unknown>),
  summary: (filters: import('./types').BettingTestFilters) =>
    get<import('./types').PaymentTestSummary>('/betting/tests/summary', filters as Record<string, unknown>),
  getTest: (id: string) => get<import('./types').PaymentTestDetail>(`/betting/tests/${id}`),
  createManualTest: (data: {
    serviceId: string
    carrierId?: string | null
    adChannel?: string | null
    connection?: string | null
    direction?: import('./types').TestDirection
  }) =>
    post<import('./types').PaymentTestDetail>('/betting/tests', data),
  updateTest: (id: string, data: Record<string, unknown>) =>
    patch<import('./types').PaymentTestDetail>(`/betting/tests/${id}`, data),
  updateSteps: (id: string, steps: Array<{ evidenceKey: string | null; label: string }>) =>
    patch<import('./types').PaymentTestDetail>(`/betting/tests/${id}/steps`, { steps }),

  /** Dépôt d'une capture : envoi multipart, le jeton reste dans l'en-tête. */
  uploadEvidence: async (id: string, file: File, label: string) => {
    const form = new FormData()
    form.append('file', file)
    form.append('label', label)
    return handleResponse<import('./types').PaymentTestDetail>(
      await fetch(scopedUrl(`/betting/tests/${id}/evidence`), { method: 'POST', headers: authHeaders(), body: form }),
    )
  },

  /**
   * Charge une preuve en blob authentifié et renvoie une URL locale.
   * Les preuves ne sont jamais servies par simple lien.
   */
  evidenceObjectUrl: async (id: string, key: string): Promise<string> => {
    const res = await fetch(scopedUrl(`/betting/tests/${id}/evidence`, { key }), { headers: authHeaders() })
    if (!res.ok) throw new Error(`API ${res.status}`)
    return URL.createObjectURL(await res.blob())
  },

  /**
   * Télécharge le ticket de surveillance d'un test.
   *
   * Le PDF passe par l'API avec le jeton : il contient des captures, il ne peut
   * pas être servi par simple lien.
   */
  downloadTicket: async (id: string, testNumber: number): Promise<void> => {
    const res = await fetch(scopedUrl(`/betting/tests/${id}/ticket.pdf`), { headers: authHeaders() })
    if (!res.ok) throw new Error(`API ${res.status}`)
    const url = URL.createObjectURL(await res.blob())
    const a = document.createElement('a')
    a.href = url
    a.download = `ticket-surveillance-${testNumber}.pdf`
    a.click()
    URL.revokeObjectURL(url)
  },

  // Référentiels
  listCarriers: (country?: string) => get<import('./types').Carrier[]>('/betting/carriers', { country }),
  createCarrier: (data: { name: string; country: string }) => post<import('./types').Carrier>('/betting/carriers', data),
  deleteCarrier: (id: string) => del(`/betting/carriers/${id}`),
  listAggregators: () => get<import('./types').PaymentAggregator[]>('/betting/aggregators'),
  createAggregator: (data: Partial<import('./types').PaymentAggregator>) =>
    post<import('./types').PaymentAggregator>('/betting/aggregators', data),
  updateAggregator: (id: string, data: Partial<import('./types').PaymentAggregator>) =>
    patch<import('./types').PaymentAggregator>(`/betting/aggregators/${id}`, data),
  importAggregators: (rows: Array<Partial<import('./types').PaymentAggregator> & { name: string }>) =>
    post<{ created: number; updated: number }>('/betting/aggregators/import', { rows }),
  listCandidates: (status?: 'PENDING' | 'LINKED' | 'IGNORED', includeFixtures?: boolean) =>
    get<import('./types').AggregatorCandidate[]>('/betting/candidates', {
      status,
      includeFixtures: includeFixtures ? '1' : undefined,
    }),
  qualifyCandidate: (id: string, decision: { aggregatorId?: string; ignore?: boolean }) =>
    patch<import('./types').AggregatorCandidate>(`/betting/candidates/${id}`, decision),

  // Recherche de nouveaux sites de paris
  /** Interroge les sources et rend les candidats, sans rien ecrire en base. */
  discover: (body: { country: string; maxQueries?: number; sources?: import('./types').BettingDiscoverySource[] }) =>
    post<import('./types').DiscoveryReport>('/betting/discover', body),
  /** Sans `apply`, le plan est calcule mais rien n'est ecrit. */
  importServices: (entries: import('./types').ImportEntry[], apply = false) =>
    post<{ applied: boolean; decisions: import('./types').ImportDecision[] }>('/betting/services/import', {
      entries,
      apply,
    }),

  // Vocabulaire de la recherche de sites
  listDiscoveryTerms: (kind?: import('./types').DiscoveryTermKind) =>
    get<import('./types').DiscoveryTerm[]>('/betting/discovery/terms', { kind }),
  createDiscoveryTerm: (data: Omit<import('./types').DiscoveryTerm, 'id'>) =>
    post<import('./types').DiscoveryTerm>('/betting/discovery/terms', data),
  updateDiscoveryTerm: (id: string, data: Partial<import('./types').DiscoveryTerm>) =>
    patch<import('./types').DiscoveryTerm>(`/betting/discovery/terms/${id}`, data),
  deleteDiscoveryTerm: (id: string) => del(`/betting/discovery/terms/${id}`),

  // Journal des candidats et rendement du vocabulaire
  /** Candidats consignes par les passes. Les ecartes sont la matiere utile. */
  listDiscoveryCandidates: (query: {
    country?: string
    status?: import('./types').DiscoveryCandidateStatus
    limit?: number
  }) => get<import('./types').LoggedDiscoveryCandidate[]>('/betting/discovery/candidates', query),
  /** Consigne le jugement de l'agent, pour les retenus comme pour les ecartes. */
  decideDiscoveryCandidates: (
    country: string,
    decisions: Array<{
      domain: string
      status: Exclude<import('./types').DiscoveryCandidateStatus, 'PENDING'>
      reason?: string | null
    }>,
  ) =>
    post<{ updated: number; unknown: string[] }>('/betting/discovery/candidates/decisions', { country, decisions }),
  /** Ce que chaque terme a remonte, et ce qu'il en est advenu. */
  vocabularyYield: (country?: string) =>
    get<import('./types').VocabularyYield>('/betting/discovery/vocabulary-yield', { country }),
}
